import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { CheckboxDropdown, Switch, TagInput } from "../../../components/ui";
import LanguageOptions, {
  LANGUAGES,
} from "../../../components/LanguageOptions";
import {
  QUILL_FORMATS,
  QUILL_MODULES,
} from "../../../components/editor/quillConfig";
import { getAllGenres } from "../../../services/genres";
import { readImageFile } from "../../../utils/files";
import "../ads/ad-form.css";
import "./content-form.css";

export const EMPTY_CONTENT = {
  title: "",
  description: "",
  status: true,
  language: "",
  language_code: "",
  release_date: "",
  genres: [], // ids
  seo_tags: [],
  jw_tags: [],
  content_type: "default",
  price: 0,
  monetization: false,
  availability: "upcoming",
  imageBase64: "", // newly picked banner (data URL)
  bannerUrl: "", // current banner of an existing content
};

/**
 * Body of the create requests (movie, TV show, podcast). `extra` holds the
 * type specific fields (e.g. `{ category: "podcast" }`).
 */
export const contentPayload = (values, extra = {}) => ({
  imageBase64: values.imageBase64,
  description: values.description,
  release_year: values.release_date ? new Date(values.release_date) : "",
  title: values.title,
  status: values.status,
  availability: "upcoming",
  isThumbnailSelected: Boolean(values.imageBase64),
  genres: values.genres,
  seo_tags: values.seo_tags,
  jw_tags: values.jw_tags,
  content_type: values.content_type,
  monetization: values.monetization,
  mime_type: "video/mp4",
  default_language: values.language,
  language_code: values.language_code,
  ...extra,
  price: values.content_type === "exclusive" ? values.price : 0,
});

/**
 * First validation error of a payload (translation key), or null. Every
 * field but the banner and the price is required, with at least one genre;
 * an active content needs a banner, an exclusive one a price.
 */
export const contentError = (payload, missingKey) => {
  const missing = Object.entries(payload).some(
    ([key, value]) =>
      key !== "imageBase64" &&
      key !== "price" &&
      (value === null ||
        value === undefined ||
        value === "" ||
        value === "default" ||
        (key === "genres" && value.length === 0))
  );
  if (missing) return missingKey;
  if (payload.status && !payload.imageBase64) {
    return "Please upload banner image!";
  }
  if (payload.content_type === "exclusive" && !payload.price) {
    return "Please enter a price for exclusive content!";
  }
  return null;
};

/**
 * Validation of the update requests (`thumbnailImageBase64` body): required
 * fields and at least one genre, a price for exclusive content, and a banner
 * for an active content that has none yet.
 */
export const contentUpdateError = (payload, hasBanner) => {
  const missing = Object.entries(payload).some(
    ([key, value]) =>
      key !== "thumbnailImageBase64" &&
      key !== "price" &&
      (value === undefined ||
        value === "" ||
        value === "default" ||
        (key === "genres" && value.length === 0))
  );
  if (missing) return "Please fill all the mandatory fields!";
  if (payload.content_type === "exclusive" && !(payload.price > 0)) {
    return "Please enter a price for exclusive content!";
  }
  if (!hasBanner && payload.status && !payload.thumbnailImageBase64) {
    return "Please upload banner image!";
  }
  return null;
};

/** Price sent by an update: 0 unless the content is exclusive. */
export const exclusivePrice = (values) =>
  values.content_type === "exclusive" &&
  values.price !== undefined &&
  values.price !== ""
    ? values.price
    : 0;

/** Release date sent by an update: the stored value when left unchanged. */
export const updatedReleaseYear = (date, stored) =>
  stored && date === new Date(stored).toISOString().split("T")[0]
    ? stored
    : date
    ? new Date(date)
    : "";

/** Genres of a content type, as `[{ _id, name }]`. */
function useGenres(genreType) {
  const [genres, setGenres] = useState([]);
  useEffect(() => {
    if (genreType) {
      getAllGenres(genreType).then((result) => setGenres(result || []));
    }
  }, [genreType]);
  return genres;
}

/**
 * General information of a movie / TV show / podcast.
 *
 * - `validate(values)`: error translation key or null (defaults to the
 *   creation checks, see `contentError`)
 * - `onSubmit(values)`: performs the request; `values.genres` only keeps
 *   genres that are still active
 * - `canRelease`: availability can be switched to "released" (edit mode,
 *   once the media is uploaded)
 * - `options`: extra switches of the publication card, as
 *   `[{ field, label, onLabel, offLabel, hint, showIf(values) }]`
 * - `genreType`: genres to choose from (no genre field when omitted)
 * - `pricing`: shows the content type and price (default true)
 * - `leadFields(values, set)`: fields shown first (e.g. an episode season)
 * - `onCancel`: cancel handler, instead of a link to `cancelPath`
 */
const ContentInfoForm = ({
  genreType,
  initialValues = EMPTY_CONTENT,
  extraPayload,
  missingKey,
  validate,
  onSubmit,
  submitLabel,
  cancelPath,
  canRelease,
  options = [],
  pricing = true,
  leadFields,
  onCancel,
}) => {
  const { t } = useTranslation();
  const genres = useGenres(genreType);
  const [values, setValues] = useState(initialValues);
  const [bannerName, setBannerName] = useState("");
  const [saving, setSaving] = useState(false);
  // The page may remount the form once saved (fresh values).
  const mounted = useRef(true);
  useEffect(() => () => (mounted.current = false), []);
  const set = (field) => (value) =>
    setValues((current) => ({ ...current, [field]: value }));

  const genreNames = genres.map((genre) => genre.name);
  const selectedGenreNames = genres
    .filter((genre) => values.genres.includes(genre._id))
    .map((genre) => genre.name);

  const handleSubmit = async (event) => {
    event.preventDefault();
    // Genres that were disabled since are dropped (as the legacy forms did).
    const submitted =
      genres.length > 0
        ? {
            ...values,
            genres: values.genres.filter((id) =>
              genres.some((genre) => genre._id === id)
            ),
          }
        : values;
    const error = validate
      ? validate(submitted)
      : contentError(contentPayload(submitted, extraPayload), missingKey);
    if (error) {
      toast.error(t(error));
      return;
    }
    setSaving(true);
    try {
      await onSubmit(submitted);
    } finally {
      if (mounted.current) setSaving(false);
    }
  };
  const banner = values.imageBase64 || values.bannerUrl;

  return (
    <form className="content-form" onSubmit={handleSubmit}>
      <div className="content-form-grid">
        <section className="ui-card">
          <div className="ui-form content-form-body">
            {leadFields && leadFields(values, set)}
            <div className="ui-field ui-field-full">
              <label htmlFor="content-title">{t("Title")}</label>
              <input
                id="content-title"
                className="ui-input"
                value={values.title}
                onChange={(e) => set("title")(e.target.value)}
              />
            </div>
            <div className="ui-field ui-field-full">
              <span className="ui-label">{t("desc")}</span>
              <ReactQuill
                className="ui-editor"
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                value={values.description}
                onChange={set("description")}
              />
            </div>
            <div className="ui-field">
              <label htmlFor="content-language">{t("Default Language")}</label>
              <select
                id="content-language"
                className="ui-input"
                value={
                  LANGUAGES.find(({ value }) =>
                    value.startsWith(`${values.language} `)
                  )?.value || "default"
                }
                onChange={(e) => {
                  const [language, code] = e.target.value.split(" ");
                  setValues((current) => ({
                    ...current,
                    language,
                    language_code: code,
                  }));
                }}
              >
                <option disabled value="default">
                  {t("choose default lang")}
                </option>
                <LanguageOptions />
              </select>
            </div>
            <div className="ui-field">
              <label htmlFor="content-release">{t("Release Date")}</label>
              <input
                id="content-release"
                className="ui-input"
                type="date"
                value={values.release_date}
                onKeyDown={(e) => e.preventDefault()}
                onChange={(e) => set("release_date")(e.target.value)}
              />
            </div>
            {genreType && (
              <div className="ui-field ui-field-full">
                <span className="ui-label">{t("Genre")}</span>
                <CheckboxDropdown
                  label={t("Choose genres")}
                  options={genreNames}
                  value={selectedGenreNames}
                  onChange={(names) =>
                    set("genres")(
                      genres
                        .filter((genre) => names.includes(genre.name))
                        .map((genre) => genre._id)
                    )
                  }
                />
              </div>
            )}
            <div className="ui-field">
              <label htmlFor="content-tags">{t("tags")}</label>
              <TagInput
                id="content-tags"
                value={values.jw_tags}
                onChange={set("jw_tags")}
                placeholder={t("Type and press Enter")}
              />
            </div>
            <div className="ui-field">
              <label htmlFor="content-seo">{t("Seo Keywords")}</label>
              <TagInput
                id="content-seo"
                value={values.seo_tags}
                onChange={set("seo_tags")}
                placeholder={t("Type and press Enter")}
              />
            </div>
          </div>
        </section>

        <div className="content-form-side">
          <section className="ui-card content-side-card">
            <h2 className="content-side-title">{t("Banner")}</h2>
            <div className="content-banner">
              {banner ? (
                <img src={banner} alt="" />
              ) : (
                <span className="ad-banner-empty" aria-hidden="true">
                  <i className="ri-image-line"></i>
                </span>
              )}
            </div>
            <div className="content-banner-actions">
              <label htmlFor="content-banner" className="ad-upload">
                <i className="ri-upload-2-line" aria-hidden="true"></i>
                {bannerName || t("upload banner")}
              </label>
              <input
                id="content-banner"
                type="file"
                accept=".png, .jpg, .jpeg"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (!file) return;
                  setBannerName(file.name);
                  readImageFile(file, set("imageBase64"));
                  e.target.value = "";
                }}
              />
              {values.imageBase64 && (
                <button
                  type="button"
                  className="ui-icon-btn"
                  data-tone="danger"
                  title={t("Delete")}
                  aria-label={t("Delete")}
                  onClick={() => {
                    set("imageBase64")("");
                    setBannerName("");
                  }}
                >
                  <i className="ri-delete-bin-line" aria-hidden="true"></i>
                </button>
              )}
            </div>
          </section>

          <section className="ui-card content-side-card">
            <h2 className="content-side-title">{t("Publication")}</h2>
            <div className="content-setting">
              <span>{t("status")}</span>
              <Switch
                checked={values.status}
                onChange={set("status")}
                label={t("status")}
                onLabel={t("Active")}
                offLabel={t("Inactive")}
              />
            </div>
            <div className="content-setting">
              <span>{t("availability")}</span>
              {canRelease ? (
                <Switch
                  checked={values.availability === "released"}
                  onChange={(released) =>
                    set("availability")(released ? "released" : "upcoming")
                  }
                  label={t("availability")}
                  onLabel={t("Released")}
                  offLabel={t("Upcoming")}
                />
              ) : (
                <strong>
                  {values.availability === "released"
                    ? t("Released")
                    : t("Upcoming")}
                </strong>
              )}
            </div>
            <div className="content-setting">
              <span>{t("Monetization")}</span>
              <Switch
                checked={values.monetization}
                onChange={set("monetization")}
                label={t("Monetization")}
                onLabel={t("On")}
                offLabel={t("Off")}
              />
            </div>
            {options
              .filter((option) => !option.showIf || option.showIf(values))
              .map((option) => (
                <React.Fragment key={option.field}>
                  <div className="content-setting">
                    <span>{option.label}</span>
                    <Switch
                      checked={Boolean(values[option.field])}
                      onChange={set(option.field)}
                      label={option.label}
                      onLabel={option.onLabel || t("Yes")}
                      offLabel={option.offLabel || t("No")}
                    />
                  </div>
                  {option.hint && <p className="ui-hint">{option.hint}</p>}
                </React.Fragment>
              ))}
            {pricing && (
              <div className="ui-field">
                <label htmlFor="content-type">{t("content type")}</label>
                <select
                  id="content-type"
                  className="ui-input"
                  value={values.content_type}
                  onChange={(e) => set("content_type")(e.target.value)}
                >
                  <option disabled value="default">
                    {t("content type")}
                  </option>
                  <option value="free">{t("Free")}</option>
                  <option value="subscription">{t("Subscription")}</option>
                  <option value="exclusive">{t("Exclusive")}</option>
                </select>
              </div>
            )}
            {pricing && values.content_type === "exclusive" && (
              <div className="ui-field">
                <label htmlFor="content-price">{t("Price")}</label>
                <div className="ui-input-group">
                  <input
                    id="content-price"
                    className="ui-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={Number.isNaN(values.price) ? "" : values.price}
                    onChange={(e) => set("price")(parseFloat(e.target.value))}
                  />
                  <span className="ui-input-addon" aria-hidden="true">
                    $
                  </span>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      <div className="content-form-footer">
        {onCancel ? (
          <button
            type="button"
            className="ui-btn ui-btn-secondary"
            onClick={onCancel}
          >
            {t("cancel")}
          </button>
        ) : (
          <Link to={cancelPath} className="ui-btn ui-btn-secondary">
            {t("cancel")}
          </Link>
        )}
        <button type="submit" className="ui-btn" disabled={saving}>
          {saving && <span className="ad-spinner" aria-hidden="true" />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
};

export default ContentInfoForm;
